/**
 * playbackDeviceContext.js
 *
 * Cross-card coordination for ONE device (one PlaybackTab view = one
 * vehicle = one device_serial). Everything else is per-card state living
 * inside useCardPlayback — this context exists only for the two facts that
 * are legitimately shared across every card of the same device:
 *
 *  - `uploadingCard`: which card (if any) currently owns the device's ONE
 *    playback upload slot. Used only to write the preemption-cost tooltip
 *    hint on every OTHER card (handoff §4.4 point 6 / §9 trap #5 successor:
 *    "Do not grey out or block other cards — the click is always allowed;
 *    use a tooltip that says what it will cost"). It never disables or
 *    blocks anything.
 *  - `lastRequestedCardKey`: the fallback recipient for an ack/error that
 *    carries no segment_uid at all (busy/not found) — mock FE
 *    `routeReplyToCard()`: "the card whose click is still outstanding".
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export const PlaybackDeviceContext = createContext(null);

export function usePlaybackDeviceProvider(deviceSerial) {
  const [uploadingCard, setUploadingCard] = useState(null); // {cardKey, cameraNum, slotId, clickAt} | null
  const lastRequestedCardKeyRef = useRef(null);

  const claimUploading = useCallback((info) => setUploadingCard(info), []);
  const releaseUploading = useCallback((key) => {
    setUploadingCard((cur) => (cur && cur.cardKey === key ? null : cur));
  }, []);
  const setLastRequestedCardKey = useCallback((key) => { lastRequestedCardKeyRef.current = key; }, []);
  const getLastRequestedCardKey = useCallback(() => lastRequestedCardKeyRef.current, []);

  // Memoized: an unmemoized object here gets a NEW identity on every render
  // of the provider. Since ~100 sibling ClipCards all read this same value,
  // and each card's playback_ack subscription effect depends on it
  // (transitively, via isMine/handleAck/handleDone), a fresh identity forces
  // EVERY card to tear down and re-subscribe its transport listener --
  // verified with Playwright to be the other half of the "Xem hangs forever"
  // bug: that mass resubscribe, combined with playbackTransport.js iterating
  // its listeners Set directly (fixed separately), turned one real
  // playback_ack into an unbounded loop that never let the decode pipeline
  // survive long enough to render a frame.
  return useMemo(() => ({
    deviceSerial,
    uploadingCard,
    claimUploading,
    releaseUploading,
    setLastRequestedCardKey,
    getLastRequestedCardKey,
  }), [deviceSerial, uploadingCard, claimUploading, releaseUploading, setLastRequestedCardKey, getLastRequestedCardKey]);
}

export function usePlaybackDeviceContext() {
  const ctx = useContext(PlaybackDeviceContext);
  if (!ctx) throw new Error('usePlaybackDeviceContext must be used within PlaybackDeviceContext.Provider');
  return ctx;
}

/**
 * Honest ETA for the preemption-cost tooltip, in seconds. Deliberately
 * sourced from bufferedSec/durationMs (the same monotonic, non-byte source
 * §7.4 requires for the clip timeline) rather than a second, bytes-derived
 * progress metric — trap #3 ("progress from total_bytes") applies just as
 * much to an estimate as to the timeline itself.
 */
export function estimateRemainingSeconds(uploadingCard) {
  if (!uploadingCard || uploadingCard.clickAt == null) return null;
  const elapsedMs = performance.now() - uploadingCard.clickAt;
  const durationSec = (uploadingCard.durationMs || 0) / 1000;
  if (durationSec <= 0) return null;
  const fraction = (uploadingCard.bufferedSec || 0) / durationSec;
  if (fraction <= 0) return null;
  const totalMs = elapsedMs / fraction;
  return Math.max(0, Math.round((totalMs - elapsedMs) / 1000));
}

export function preemptHintMessage(uploadingCard) {
  const remain = estimateRemainingSeconds(uploadingCard);
  return remain != null
    ? `Sẽ DỪNG đoạn đang tải (còn ≈${remain}s) để tải đoạn này — thiết bị chỉ tải một đoạn một lúc`
    : 'Sẽ DỪNG đoạn đang tải để tải đoạn này — thiết bị chỉ tải một đoạn một lúc';
}
